import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Animated, Platform, Pressable, Text, TextInput, View } from 'react-native';
import { ChevronLeft } from 'lucide-react-native';
import { useTheme } from '../../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../../src/shared/constants/animations';
import { sanitizeInput } from '../../../../src/helpers/sanitizeInput.helper';
import {
  JOIN_WC_GROUP_COPY,
  JOIN_WC_INVITE_CODE_LENGTH,
} from '../JoinWorldCupGroupScreen.constants';
import { getJoinCodeEntryStyles } from './JoinCodeEntry.styles';

interface JoinCodeEntryProps {
  code: string;
  onChange: (code: string) => void;
  onBack: () => void;
  error: string | null;
  isLoading: boolean;
}

type EntryStyles = ReturnType<typeof getJoinCodeEntryStyles>;

function OtpBox({
  char,
  isActive,
  isFilled,
  isValidating,
  caretAnim,
  styles,
}: {
  char: string;
  isActive: boolean;
  isFilled: boolean;
  isValidating: boolean;
  caretAnim: Animated.Value;
  styles: EntryStyles;
}) {
  return (
    <View
      style={[
        styles.otpBox,
        isFilled && styles.otpBoxFilled,
        isActive && styles.otpBoxActive,
        isValidating && isFilled && styles.otpBoxValidating,
      ]}
    >
      {isFilled ? (
        <Text style={[styles.otpChar, isValidating && styles.otpCharValidating]}>{char}</Text>
      ) : isActive ? (
        <Animated.View style={[styles.caret, { opacity: caretAnim }]} />
      ) : null}
    </View>
  );
}

export function JoinCodeEntry({ code, onChange, onBack, error, isLoading }: JoinCodeEntryProps) {
  const theme = useTheme();
  const styles = useMemo(() => getJoinCodeEntryStyles(theme), [theme]);
  const inputRef = useRef<TextInput>(null);
  const [isFocused, setIsFocused] = useState(false);
  const caretAnim = useRef(new Animated.Value(1)).current;
  const wasLoadingRef = useRef(false);
  const hoverWeb = Platform.select({ web: WEB_HOVER_TRANSITION_STYLE });

  // Re-focus the hidden input when loading finishes so the keyboard stays up
  useEffect(() => {
    if (wasLoadingRef.current && !isLoading) {
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
    wasLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    if (!isFocused) {
      caretAnim.setValue(1);
      return;
    }
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(caretAnim, { toValue: 0, duration: 450, useNativeDriver: true }),
        Animated.timing(caretAnim, { toValue: 1, duration: 450, useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [caretAnim, isFocused]);

  const handleChangeText = useCallback(
    (text: string) => {
      const clean = sanitizeInput(text, true)
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, JOIN_WC_INVITE_CODE_LENGTH);
      onChange(clean);
    },
    [onChange],
  );

  const padded = code.padEnd(JOIN_WC_INVITE_CODE_LENGTH, ' ');

  return (
    <View style={styles.container}>
      <Pressable
        onPress={onBack}
        style={({ pressed, hovered }) => [
          styles.backButton,
          hoverWeb,
          pressed && styles.backButtonPressed,
          hovered && Platform.OS === 'web' && styles.backButtonHover,
        ]}
      >
        <ChevronLeft size={18} color={theme.primary} />
        <Text style={styles.backButtonText}>{JOIN_WC_GROUP_COPY.BACK}</Text>
      </Pressable>

      <View style={styles.badgeChip}>
        <View style={styles.badgeChipDot} />
        <Text style={styles.badgeChipText}>{JOIN_WC_GROUP_COPY.BADGE_LABEL}</Text>
      </View>

      <Text style={styles.title}>{JOIN_WC_GROUP_COPY.ENTRY_TITLE}</Text>
      <Text style={styles.subtitle}>{JOIN_WC_GROUP_COPY.ENTRY_SUBTITLE}</Text>

      {/*
        OTP area: transparent TextInput overlaid on the visual boxes so any
        tap in this region naturally focuses the input. The boxes are
        pointer-events:none so touches pass straight through to the TextInput.
      */}
      <View style={styles.otpWrapper}>
        <View style={styles.otpBoxRow} pointerEvents="none">
          {[0, 1, 2].map(i => (
            <OtpBox
              key={i}
              char={padded[i]}
              isActive={isFocused && code.length === i}
              isFilled={padded[i] !== ' '}
              isValidating={isLoading}
              caretAnim={caretAnim}
              styles={styles}
            />
          ))}
          <View style={[styles.otpDash, code.length >= 3 && styles.otpDashFilled]} />
          {[3, 4, 5].map(i => (
            <OtpBox
              key={i}
              char={padded[i]}
              isActive={isFocused && code.length === i}
              isFilled={padded[i] !== ' '}
              isValidating={isLoading}
              caretAnim={caretAnim}
              styles={styles}
            />
          ))}
        </View>

        {/* Transparent overlay — blocked while loading so user can't type mid-fetch */}
        <TextInput
          ref={inputRef}
          value={code}
          onChangeText={handleChangeText}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          editable={!isLoading}
          autoFocus
          autoCapitalize="characters"
          autoCorrect={false}
          caretHidden
          style={styles.otpTextInput}
          testID="join-world-cup-group-code"
        />
        {isLoading && (
          <ActivityIndicator
            size="small"
            color={theme.primary}
            style={styles.otpLoadingOverlay}
          />
        )}
      </View>

      {isLoading ? (
        <Text style={[styles.statusText, styles.statusTextValidating]}>
          {JOIN_WC_GROUP_COPY.VALIDATING}
        </Text>
      ) : error ? (
        <Text style={[styles.statusText, styles.statusTextError]}>{error}</Text>
      ) : (
        <Text style={styles.statusText}>
          {JOIN_WC_GROUP_COPY.CODE_HELPER}{' '}
          <Text style={styles.helperCode}>{JOIN_WC_GROUP_COPY.CODE_PLACEHOLDER}</Text>
        </Text>
      )}
    </View>
  );
}
